import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHelmet from '../components/SEOHelmet';

const HOSTING_PLANS = [
  {
    name: 'Managed Website',
    price: '$19',
    desc: 'For business websites, landing pages, blogs, and WordPress sites.',
    features: ['SSL and DNS setup', 'Daily backups', 'CDN configuration', 'Uptime checks', 'Security updates'],
    highlighted: false,
  },
  {
    name: 'Managed Business',
    price: '$49',
    desc: 'For growing sites, e-commerce stores, portals, and higher traffic operations.',
    features: ['Priority support', 'Performance tuning', 'WAF and hardening', 'Staging workflow', 'Monthly report'],
    highlighted: true,
  },
  {
    name: 'Managed Cloud',
    price: '$99',
    desc: 'For SaaS apps, APIs, dashboards, and production systems that need stronger reliability.',
    features: ['Cloud deployment', 'Monitoring and alerts', 'Container/server setup', 'Backup restore drills', 'Scaling support'],
    highlighted: false,
  },
];

const HOSTING_FEATURES = [
  ['MG', 'Managed migration', 'We move your website or app with DNS, SSL, redirects, and launch checks handled.'],
  ['BK', 'Backups and restore', 'Automatic backups plus restore support so recovery is not a guessing game.'],
  ['PF', 'Performance tuning', 'CDN, caching, image delivery, server tuning, and speed improvements.'],
  ['SC', 'Security baseline', 'SSL, WAF, access review, updates, hardening, and practical monitoring.'],
  ['UP', 'Uptime monitoring', 'Checks and alerts for downtime, service problems, and production incidents.'],
  ['SP', 'Human support', 'A technical team that understands web apps, WordPress, SaaS, and cloud infrastructure.'],
];

const FAQS = [
  ['Can you migrate my current website?', 'Yes. We handle migration planning, files, database, DNS, SSL, and launch checks.'],
  ['Do you host WordPress?', 'Yes. We support managed WordPress hosting, updates, backups, security, and performance tuning.'],
  ['Can I host a React or Node.js app?', 'Yes. We can manage static frontends, APIs, dashboards, Node.js apps, and cloud deployments.'],
  ['Do you include SSL?', 'Yes. SSL setup is included, and we can also configure CDN and WAF protection when needed.'],
  ['What if my site goes down?', 'We prioritize production incidents and use the support severity model to respond based on business impact.'],
];

export default function HostingPage() {
  const [expandedFaq, setExpandedFaq] = useState(0);

  return (
    <>
      <SEOHelmet
        title="Managed Hosting | AxentraLab"
        description="Managed hosting for websites, WordPress, e-commerce, SaaS apps, APIs, and business systems with SSL, backups, CDN, security, and support."
        keywords="managed hosting, web hosting, cloud hosting, WordPress hosting, SaaS hosting"
      />
      <div style={{ background: 'var(--bg)', minHeight: '100vh', overflowX: 'hidden' }}>
        <style>{`
          @keyframes fadeUp { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: none; } }
          .hosting-shell { max-width: 1180px; margin: 0 auto; padding: 0 5%; }
          .hosting-label {
            display: inline-flex; align-items: center; gap: 10px; color: var(--teal);
            font-family: var(--font-m); font-size: 12px; font-weight: 800; letter-spacing: 1.5px;
            text-transform: uppercase; margin-bottom: 18px;
          }
          .hosting-label::before { content: ''; width: 28px; height: 2px; border-radius: 99px; background: var(--teal); }
          .stats-grid, .feature-grid, .plan-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px; }
          .stats-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); }
          .hosting-card {
            border: 1px solid var(--border); border-radius: 8px; background: #fff; padding: 24px;
            transition: transform .2s ease, border-color .2s ease, box-shadow .2s ease;
          }
          .hosting-card:hover { transform: translateY(-4px); border-color: rgba(37,99,235,.3); box-shadow: 0 20px 44px -30px rgba(15,23,42,.45); }
          .code-box {
            width: 44px; height: 44px; border-radius: 8px; display: grid; place-items: center;
            background: rgba(37,99,235,.09); color: var(--teal); font-family: var(--font-m);
            font-size: 13px; font-weight: 900; margin-bottom: 18px;
          }
          .faq-button {
            width: 100%; min-height: 72px; padding: 22px 24px; border: 0; background: transparent;
            color: var(--text); text-align: left; display: flex; justify-content: space-between; gap: 18px;
            align-items: center; font-size: 16px; font-weight: 800;
          }
          @media (max-width: 980px) { .stats-grid, .feature-grid, .plan-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } .cta-layout { grid-template-columns: 1fr !important; } }
          @media (max-width: 680px) { .stats-grid, .feature-grid, .plan-grid { grid-template-columns: 1fr; } .hero-title { font-size: 42px !important; } }
        `}</style>

        <section style={{ padding: '150px 0 76px', position: 'relative', overflow: 'hidden', background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)' }}>
          <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(var(--border) 1px,transparent 1px),linear-gradient(90deg,var(--border) 1px,transparent 1px)', backgroundSize: '64px 64px', opacity: .44 }} />
          <div className="hosting-shell" style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ maxWidth: 880, animation: 'fadeUp .7s ease both' }}>
              <div className="hosting-label">Managed Hosting</div>
              <h1 className="hero-title" style={{ fontFamily: 'var(--font-h)', fontSize: 'clamp(48px,7vw,86px)', lineHeight: .98, color: 'var(--text)', margin: '0 0 24px', fontWeight: 900 }}>
                Hosting managed by people who understand your software.
              </h1>
              <p style={{ color: 'var(--muted)', fontSize: 18, lineHeight: 1.75, maxWidth: 680, marginBottom: 34 }}>
                We manage hosting, migration, SSL, DNS, CDN, backups, uptime checks, security basics, and support for websites, stores, SaaS apps, and business systems.
              </p>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <a href="#hosting-plans" className="btn-primary" style={{ padding: '15px 30px' }}>View Plans -&gt;</a>
                <Link to="/contact" className="btn-outline" style={{ padding: '15px 28px' }}>Request Migration</Link>
              </div>
            </div>
            <div className="stats-grid" style={{ marginTop: 54 }}>
              {[
                ['99.9%', 'Uptime target'],
                ['24/7', 'Monitoring ready'],
                ['SSL', 'Included setup'],
                ['CDN', 'Performance option'],
              ].map(([value, label]) => (
                <div key={label} className="hosting-card">
                  <div style={{ fontFamily: 'var(--font-h)', fontSize: 34, fontWeight: 900, color: 'var(--text)', lineHeight: 1 }}>{value}</div>
                  <div style={{ marginTop: 8, color: 'var(--muted)', fontFamily: 'var(--font-m)', fontSize: 12 }}>{label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="hosting-shell" style={{ padding: '76px 5% 92px' }}>
          <div style={{ marginBottom: 34 }}>
            <div className="hosting-label">Managed Features</div>
            <h2 style={{ fontFamily: 'var(--font-h)', fontSize: 'clamp(30px,4.5vw,52px)', lineHeight: 1.05, color: 'var(--text)', margin: 0, fontWeight: 900 }}>
              Hosting is more than a server.
            </h2>
          </div>
          <div className="feature-grid">
            {HOSTING_FEATURES.map(([code, title, desc]) => (
              <div key={title} className="hosting-card">
                <div className="code-box">{code}</div>
                <h3 style={{ color: 'var(--text)', fontFamily: 'var(--font-h)', fontSize: 21, fontWeight: 900, margin: '0 0 10px' }}>{title}</h3>
                <p style={{ color: 'var(--muted)', fontSize: 14, lineHeight: 1.7 }}>{desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section id="hosting-plans" style={{ padding: '82px 0', background: '#fff', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
          <div className="hosting-shell">
            <div style={{ textAlign: 'center', marginBottom: 42 }}>
              <div className="hosting-label" style={{ justifyContent: 'center' }}>Plans</div>
              <h2 style={{ fontFamily: 'var(--font-h)', fontSize: 'clamp(30px,4.5vw,48px)', color: 'var(--text)', margin: 0, fontWeight: 900 }}>
                Choose a managed hosting lane.
              </h2>
            </div>
            <div className="plan-grid">
              {HOSTING_PLANS.map((plan) => (
                <div key={plan.name} className="hosting-card" style={{ borderColor: plan.highlighted ? 'rgba(37,99,235,.45)' : 'var(--border)', boxShadow: plan.highlighted ? '0 24px 60px -38px rgba(37,99,235,.6)' : undefined }}>
                  {plan.highlighted && <div style={{ color: 'var(--teal)', fontFamily: 'var(--font-m)', fontSize: 12, fontWeight: 900, textTransform: 'uppercase', marginBottom: 12 }}>Most Popular</div>}
                  <h3 style={{ fontFamily: 'var(--font-h)', color: 'var(--text)', fontSize: 26, fontWeight: 900, margin: '0 0 8px' }}>{plan.name}</h3>
                  <p style={{ color: 'var(--muted)', fontSize: 14, lineHeight: 1.65, marginBottom: 22 }}>{plan.desc}</p>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 24 }}>
                    <span style={{ fontFamily: 'var(--font-h)', color: plan.highlighted ? 'var(--teal)' : 'var(--text)', fontSize: 46, fontWeight: 900 }}>{plan.price}</span>
                    <span style={{ color: 'var(--muted)', fontSize: 14 }}>/month</span>
                  </div>
                  <Link to="/contact" className={plan.highlighted ? 'btn-primary' : 'btn-outline'} style={{ width: '100%', padding: '13px 18px', marginBottom: 24 }}>
                    Start Hosting
                  </Link>
                  <div style={{ borderTop: '1px solid var(--border)', paddingTop: 22, display: 'grid', gap: 12 }}>
                    {plan.features.map((item) => (
                      <div key={item} style={{ display: 'flex', gap: 10, color: 'var(--text)', fontSize: 14, lineHeight: 1.45 }}>
                        <span style={{ color: 'var(--teal)', fontWeight: 900 }}>-</span>
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="hosting-shell" style={{ padding: '82px 5% 94px', maxWidth: 900 }}>
          <div style={{ textAlign: 'center', marginBottom: 42 }}>
            <div className="hosting-label" style={{ justifyContent: 'center' }}>FAQ</div>
            <h2 style={{ fontFamily: 'var(--font-h)', fontSize: 'clamp(30px,4.5vw,48px)', color: 'var(--text)', margin: 0, fontWeight: 900 }}>Managed hosting questions.</h2>
          </div>
          <div style={{ display: 'grid', gap: 14 }}>
            {FAQS.map(([q, a], index) => (
              <div key={q} className="hosting-card" style={{ padding: 0, overflow: 'hidden' }}>
                <button type="button" className="faq-button" onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}>
                  <span>{q}</span>
                  <span style={{ color: 'var(--teal)', fontSize: 24 }}>{expandedFaq === index ? '-' : '+'}</span>
                </button>
                {expandedFaq === index && <div style={{ padding: '0 24px 24px', color: 'var(--muted)', fontSize: 15, lineHeight: 1.75 }}>{a}</div>}
              </div>
            ))}
          </div>
        </section>

        <section className="hosting-shell" style={{ padding: '0 5% 110px' }}>
          <div className="cta-layout" style={{ border: '1px solid var(--border)', borderRadius: 8, background: '#0f172a', color: '#fff', padding: 'clamp(42px,7vw,76px)', display: 'grid', gridTemplateColumns: '1fr auto', gap: 28, alignItems: 'center' }}>
            <div>
              <div style={{ color: '#93c5fd', fontFamily: 'var(--font-m)', fontSize: 12, fontWeight: 800, textTransform: 'uppercase', marginBottom: 16 }}>Move with confidence</div>
              <h2 style={{ fontFamily: 'var(--font-h)', fontSize: 'clamp(32px,5vw,58px)', lineHeight: 1.02, margin: '0 0 18px', fontWeight: 900 }}>
                Need hosting that will not become another problem?
              </h2>
              <p style={{ color: '#cbd5e1', fontSize: 16, lineHeight: 1.7, maxWidth: 620 }}>
                Send us your current website, stack, traffic level, and pain points. We will recommend the right managed setup.
              </p>
            </div>
            <Link to="/contact" className="btn-primary" style={{ padding: '15px 28px', background: '#fff', color: '#0f172a' }}>Request Hosting Plan</Link>
          </div>
        </section>
      </div>
    </>
  );
}
